import {Modal} from '@sollapay/ui/components';
import {GrowYourMoneyIllustration} from '@sollapay/ui/illustrations';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useUserSettingsQuery} from '@/features/user-settings';

import {TrustAccountConsentFields, TrustAccountFormFields} from '../components';
import {useCreateTrustAccountMutation} from '../hooks';

import type {TrustPurpose} from '@sollapay/enums';
import type {FC} from 'react';

type CreateTrustAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (id: string) => void;
};

export const CreateTrustAccountModal: FC<CreateTrustAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const createTrustAccountMutation = useCreateTrustAccountMutation();
  // Fetched only while the modal is open — most list-page visits never open it.
  const {data: userSettings, isLoading: isUserSettingsLoading} = useUserSettingsQuery({
    enabled: isOpen
  });
  // Hidden while settings load, so the checkboxes don't flash.
  const requiresConsent = !isUserSettingsLoading && !userSettings?.hasAcceptedTerms;

  const [name, setName] = useState('');
  const [trustPurpose, setTrustPurpose] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Blocked while settings load: the default-true checkboxes could overwrite a stored marketingConsent.
  const canCreate =
    name.trim().length > 0 &&
    trustPurpose.length > 0 &&
    !isUserSettingsLoading &&
    (!requiresConsent || termsAccepted);

  const isDirty = name !== '' || trustPurpose !== '' || !termsAccepted || !marketingConsent;
  const isCreating = createTrustAccountMutation.isPending;

  const resetFields = () => {
    setName('');
    setTrustPurpose('');
    setTermsAccepted(true);
    setMarketingConsent(true);
  };

  const handleCreate = async () => {
    if (!canCreate) return;

    setSubmitError(null);

    try {
      const response = await createTrustAccountMutation.mutateAsync({
        trustName: name.trim(),
        trustPurpose: trustPurpose as TrustPurpose,
        ...(requiresConsent ? {termsAccepted, marketingConsent} : {})
      });

      resetFields();
      onClose();
      onSuccess(response.data.id);
    } catch (error) {
      setSubmitError(error);
    }
  };

  const handleClose = () => {
    setSubmitError(null);
    resetFields();
    onClose();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDirty={isDirty}
      illustration={<GrowYourMoneyIllustration />}
      iconVariant="brand"
      titleSize="display-2xs"
      title={t('modal.title')}
      primaryLabel={t('modal.create')}
      primaryDisabled={!canCreate}
      primaryLoading={isCreating}
      onPrimaryAction={handleCreate}
      secondaryLabel={t('common:cancel')}
      onSecondaryAction={handleClose}
    >
      <div className="space-y-4 pt-1.5">
        <TrustAccountFormFields
          name={name}
          onNameChange={setName}
          trustPurpose={trustPurpose}
          onTrustPurposeChange={setTrustPurpose}
          submitError={submitError}
        />
        {requiresConsent && (
          <TrustAccountConsentFields
            termsAccepted={termsAccepted}
            onTermsAcceptedChange={setTermsAccepted}
            marketingConsent={marketingConsent}
            onMarketingConsentChange={setMarketingConsent}
          />
        )}
      </div>
    </Modal>
  );
};
